import React, { useState, useEffect } from 'react';
import parse from 'html-react-parser';
import * as api from '../../utils/api';
import * as MedlemsAdfaerdService from '@Utils/medlemsAdfaerdService';
import { IconQuestionMark } from '../../utils/icons';
import SvgTooltip from '../SvgTooltip';
import { formConfig } from '@Utils/formConfig';
import { TInput } from '../../utils/types';
import ButtonContainer from '../ButtonContainer';

export interface FormProps {
  velkomstEpiserverId: string
  introButtonText: string
  inputData: TInput
  showEmail: boolean
  showPhn: boolean
  showSalary: boolean
  setInputData: ( v : TInput ) => void
  setFormSubmitted: ( v : boolean ) => void
  setShowSalaryReceipt: ( v : boolean ) => void
  setShowModal: ( v : boolean ) => void
  showDataPolicy: boolean
  showConsent: boolean
  persondataLabel?: string
  samtykkeLabel?: string
  baseInteraktionDialogId?: string
  skipBtnText?: string
  objdata?: {[name: string]: string}
  fromRecommendation?: boolean
  handleContentSkip?: React.MouseEventHandler<HTMLButtonElement>
}

const Form = (props: FormProps) => {
	const {
		inputData,
		introButtonText,
		setInputData,
		setFormSubmitted,
		showDataPolicy,
		showConsent,
		setShowModal,
		setShowSalaryReceipt,
		showSalary,
		showEmail,
		showPhn,
		persondataLabel,
		samtykkeLabel,
		baseInteraktionDialogId,
		skipBtnText,
		objdata,
		velkomstEpiserverId,
		fromRecommendation,
		handleContentSkip
	} = props;

	const { telephone, consent, salary } = inputData;
	const [errorEmail, setErrorEmail] = useState(false);
	const [errorPhn, setErrorPhn] = useState(false);
	const [errorSalary, setErrorSalary] = useState(false);
	const [isChecked, setIsChecked] = useState(consent ? true : false);
	const [isDisabled, setIsDisabled] = useState(true);
	const [emailInput, setEmailInput] = useState(inputData.email);
	const [phnInput, setPhnInput] = useState(inputData.telephone);
	const [salaryInput, setSalaryInput] = useState(inputData.salary);
	const [consentInput, setConsentInput] = useState(inputData.consent);
	const [showSkipBtn, setShowSkipBtn] = useState(inputData.email && inputData.telephone ? false : true);
	const interaktionDialogId = objdata?.['BaseInteraktionDialogId'];

	const handleSwitch = () => {
		const newCheckedState = !isChecked;
		setIsChecked(newCheckedState);
		setConsentInput(newCheckedState);
	};

	const handleSubmit = async (e: { currentTarget: HTMLFormElement | undefined; preventDefault: () => void }) => {
		const formData = new FormData(e.currentTarget);
		e.preventDefault();

		const email = formData.get('email') as string;
		const telephone = formData.get('phn') as string;
		const salary = formData.get('salary') as string;
		const consent = formData.get('switch') as unknown;
		let validTelephone = true;

		if (telephone) {
			if (!telephone.match(formConfig.telephone)) {
				validTelephone = false;
				setErrorPhn(true);
			} else {
				setErrorPhn(false);
			}
		}

		if ((showPhn && showEmail && !showConsent && !fromRecommendation) && (email && email.match(formConfig.emailDomain)) && validTelephone) {
			setInputData({ ...inputData, email, telephone });

			if (objdata)	{
				api.createEvent(velkomstEpiserverId, objdata['HaendelseKontaktoplysninger']);
			}
			api.saveContactInfo({ ...inputData, email, telephone, interaktionDialogId });

			if (baseInteraktionDialogId) {
				MedlemsAdfaerdService.gemInteraktion('KontaktoplysningerBekræftet', baseInteraktionDialogId, '', 'WEB');
			}
			setFormSubmitted(true);
			setErrorEmail(false);
			setShowModal(false);
		} else if (email && !email.match(formConfig.emailDomain)) {
			setErrorEmail(true);
		}

		if (!showPhn && email && email.match(formConfig.emailDomain)) {
			setInputData({ ...inputData, email });

			if (objdata)	{
				api.createEvent(velkomstEpiserverId, objdata['HaendelseKontaktoplysninger']);
			}
			api.saveContactInfo({ ...inputData, email, interaktionDialogId });
			setErrorEmail(false);
			setFormSubmitted(true);
			setShowModal(false);
		} else if (email && !email.match(formConfig.emailDomain)) {
			setErrorEmail(true);
		}

		if (showConsent) {
			if ((consent === 'on' || consent === true) && emailInput && email.match(formConfig.emailDomain) && validTelephone) {
				if (baseInteraktionDialogId) {
					MedlemsAdfaerdService.gemInteraktion('SamtykkeGivet', baseInteraktionDialogId, '', 'WEB');
				}
				const signedUp = true;
				setInputData({ ...inputData, email, telephone, consent, signedUp });

				if (objdata)	{
					api.createEvent(velkomstEpiserverId, objdata['HaendelseSamtykke']);
				}
				await api.saveContactInfo({ ...inputData, email, telephone, consent, interaktionDialogId });
				setFormSubmitted(true);
				setErrorEmail(false);
				setShowModal(false);
			}
		}

		if (showSalary && salary && salary.match(formConfig.salery)) {

			const parsedSalary = salary.match(formConfig.salery)?.[0] || salary;
			setInputData({ ...inputData, salary: parsedSalary });

			if (objdata) {
				api.createEvent(velkomstEpiserverId, objdata['HaendelseLoen']);
			}
			api.saveSalary({ salary: parsedSalary });


			if (baseInteraktionDialogId) {
				MedlemsAdfaerdService.gemInteraktion('LønBekræftet', baseInteraktionDialogId, '', 'WEB');
			}
			setErrorEmail(false);
			setErrorSalary(false);
			setFormSubmitted(true);
			setShowModal(false);
			setShowSalaryReceipt(true);
		} else if (showSalary && salary && !salary.match(formConfig.salery)) {
			setErrorSalary(true);
		}

		if (fromRecommendation && email && email.match(formConfig.emailDomain) && validTelephone) {
			const signedUp = true;
			setInputData({ ...inputData, email, telephone, signedUp });
			api.saveContactInfo({ ...inputData, email, telephone, interaktionDialogId });
			setErrorEmail(false);
			setFormSubmitted(true);
		} else if (fromRecommendation && email && !email.match(formConfig.emailDomain)) {
			setErrorEmail(true);
		}
	};

	useEffect(() => {
		if (!showConsent && emailInput) {
			setIsDisabled(false);
			setShowSkipBtn(false);
		} else if (!emailInput) {
			setIsDisabled(true);
			setShowSkipBtn(true);
		}
	}, [emailInput]);

	useEffect(() => {
		if (emailInput) {
			setIsDisabled(false);
		} else if (!emailInput) {
			setIsDisabled(true);
		}
	}, [emailInput]);

	useEffect(() => {
		if (showSalary && salaryInput) {
			setIsDisabled(false);
		} else if (showSalary && !salaryInput) {
			setIsDisabled(true);
		}
	}, [salaryInput]);

	useEffect(() => {
		if (showConsent && (consentInput && emailInput)) {
			setIsDisabled(false);
		} else if (showConsent && (!consentInput || !emailInput)) {
			setIsDisabled(true);
		}
	}, [consentInput, emailInput]);

	return (
		<>
			<form onSubmit={ handleSubmit } className="lhp-form">
				{ !showDataPolicy && showPhn && <p><strong>Dine oplysninger</strong></p> }
				<fieldset className={ !showPhn && !showSalary ? 'email-only' : '' }>
					{ showEmail && (
						<label htmlFor="email"><span className="label-text">Din mail</span>
							<input
								type="text"
								name="email"
								className={ errorEmail ? 'input-text error' : 'input-text' }
								value={ emailInput }
								onFocus={ (e) => {
									if (e.target.value !== emailInput) {
										setEmailInput(e.target.value);
									}
								} }
								onChange={ (e) => setEmailInput(e.target.value) }
							/>
							{ (showDataPolicy || !showPhn) && <span className="f14 privacy">{ persondataLabel && parse(persondataLabel) }</span> }
						</label>
					) }
					{ showPhn && (
						<label htmlFor="phn"><span className="label-text">Dit mobilnummer</span>
							<input
								type="tel"
								name="phn"
								className={ errorPhn ? 'input-tel error' : 'input-tel' }
								defaultValue={ telephone }
								onFocus={ (e) => {
									if (e.target.value !== phnInput) {
										setPhnInput(e.target.value);
									}
								} }
								onChange={ (e) => setPhnInput(e.target.value) }
							/>
						</label>
					) }
					{ showSalary && (
						<label htmlFor="salary" className="salary labeltooltip">
							<span className="label-text">Din månedsløn <span data-svgtooltip><IconQuestionMark /><SvgTooltip tooltipData="Uden pension, og før der er trukket skat og AM-bidrag." /></span></span>
							<input
								type="text"
								name="salary"
								className={ errorSalary ? 'input-text error' : 'input-text' }
								defaultValue={ salary }
								onFocus={ (e) => {
									if (e.target.value !== salaryInput) {
										setSalaryInput(e.target.value);
									}
								} }
								onChange={ (e) => setSalaryInput(e.target.value) }
							/>
							<span className="salary-unit">kr./md.</span>
						</label>
					) }
					{ showConsent && (
						<div className="consent-switch">
							<label htmlFor="switch" className="switch" onClick={ handleSwitch }>
								<input type="checkbox" name="switch" onChange={ handleSwitch } checked={ isChecked } />
								<span className="toggle-switch" />
							</label>
							<p className="consent-toggle">{ samtykkeLabel }</p>
						</div>
					) }
				</fieldset>
				<ButtonContainer
					center={ !showConsent && (!!showPhn || !!showSalary) }
					m_medium
				>
					{ introButtonText && <button className="btn btn-primary" type="submit" disabled={ isDisabled }>{ introButtonText }</button> }
				</ButtonContainer>
			</form>
			{ showSkipBtn && skipBtnText && !showConsent && (
				<ButtonContainer center m_small>
					<button className="btn-text" onClick={ handleContentSkip }>
						{ skipBtnText }
					</button>
				</ButtonContainer>
			) }
		</>
	);
};

export default Form;